import { useRef, useState } from "react";
import { Upload, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api, errMsg, imgSrc } from "../../lib/api";
import { SLOTS, useSiteImagesRaw } from "../../lib/siteImages";

const Slot = ({ k, label, def, current, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      await api.put(`/admin/site-images/${k}`, { url: data.url });
      toast.success("Immagine sostituita");
      onChange();
    } catch (e) { toast.error(errMsg(e, "Caricamento non riuscito")); }
    setBusy(false);
  };
  const reset = async () => {
    if (!window.confirm("Ripristinare l’immagine originale?")) return;
    await api.delete(`/admin/site-images/${k}`);
    onChange();
  };
  return (
    <div data-testid={`site-image-${k}`}>
      <div className="relative aspect-[4/3] bg-[#F0F0F0] overflow-hidden">
        <img src={current ? imgSrc(current) : def} alt={label} className="w-full h-full object-cover" />
        {current && <span className="absolute top-2 left-2 eyebrow !text-[0.58rem] bg-[#1A1A1A] text-[#FFFFFF] px-2 py-1">Personalizzata</span>}
      </div>
      <p className="mt-3 text-sm leading-snug">{label}</p>
      <div className="mt-3 flex items-center gap-4">
        <button type="button" disabled={busy} onClick={() => ref.current?.click()} data-testid={`site-image-upload-${k}`}
          className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FFFFFF] px-4 min-h-[40px] text-[0.7rem] tracking-[0.14em] uppercase disabled:opacity-50">
          <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />{busy ? "Caricamento…" : "Sostituisci"}
        </button>
        {current && <button type="button" onClick={reset} className="inline-flex items-center gap-1 text-xs underline underline-offset-2" data-testid={`site-image-reset-${k}`}><RotateCcw className="w-3 h-3" />Originale</button>}
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
    </div>
  );
};

export const SiteImagesAdmin = () => {
  const qc = useQueryClient();
  const { data = {} } = useSiteImagesRaw();
  const refresh = () => qc.invalidateQueries({ queryKey: ["site-images"] });
  return (
    <section data-testid="admin-site-images">
      <p className="text-sm text-[#555555] max-w-2xl">Sostituisci le fotografie delle pagine del sito. Consigliato: JPG o WEBP, lato lungo almeno 2000 px, massimo 10 MB. Le foto delle pietre si gestiscono nella sezione “Lastre e blocchi”.</p>
      {SLOTS.map(([group, slots]) => (
        <div key={group} className="mt-12">
          <h2 className="f-serif font-light text-3xl border-b border-[#1A1A1A]/12 pb-3">{group}</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
            {slots.map(([k, label, def]) => <Slot key={k} k={k} label={label} def={def} current={data[k]} onChange={refresh} />)}
          </div>
        </div>
      ))}
    </section>
  );
};
